package com.kinoshazam.screens.search.components

import androidx.compose.animation.core.LinearEasing
import androidx.compose.animation.core.RepeatMode
import androidx.compose.animation.core.animateFloat
import androidx.compose.animation.core.infiniteRepeatable
import androidx.compose.animation.core.rememberInfiniteTransition
import androidx.compose.animation.core.tween
import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material3.Icon
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Path
import androidx.compose.ui.graphics.compositeOver
import androidx.compose.ui.graphics.drawscope.DrawScope
import androidx.compose.ui.graphics.drawscope.rotate
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.semantics.Role
import androidx.compose.ui.semantics.role
import androidx.compose.ui.semantics.selected
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import com.kinoshazam.uiKit.theme.UiKitTheme
import kinoshazam.resources.generated.resources.Res
import kinoshazam.resources.generated.resources.identify_wand
import kinoshazam.resources.generated.resources.magic_wand
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource

@Composable
fun IdentifyWandButton(
    selected: Boolean,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    size: Dp = 44.dp,
    contentDescription: String? = null,
) {
    val accent = UiKitTheme.colors.color_1
    val description = contentDescription ?: stringResource(Res.string.identify_wand)
    val infinite = rememberInfiniteTransition(label = "identify_wand")
    val twinkle by infinite.animateFloat(
        initialValue = 0.35f,
        targetValue = 1f,
        animationSpec = infiniteRepeatable(
            animation = tween(durationMillis = 900, easing = LinearEasing),
            repeatMode = RepeatMode.Reverse,
        ),
        label = "wand_twinkle",
    )
    val orbit by infinite.animateFloat(
        initialValue = 0f,
        targetValue = 360f,
        animationSpec = infiniteRepeatable(
            animation = tween(durationMillis = if (selected) 4800 else 14000, easing = LinearEasing),
        ),
        label = "wand_orbit",
    )
    val pulse by infinite.animateFloat(
        initialValue = 1f,
        targetValue = if (selected) 1.08f else 1.02f,
        animationSpec = infiniteRepeatable(
            animation = tween(durationMillis = 1100, easing = LinearEasing),
            repeatMode = RepeatMode.Reverse,
        ),
        label = "wand_pulse",
    )
    val interactionSource = remember { MutableInteractionSource() }

    Box(
        modifier = modifier
            .size(size)
            .graphicsLayer {
                scaleX = pulse
                scaleY = pulse
            }
            .clip(CircleShape)
            .semantics {
                role = Role.Switch
                this.selected = selected
            }
            .background(
                if (selected) {
                    accent.copy(alpha = 0.22f).compositeOver(UiKitTheme.colors.color_4)
                } else {
                    UiKitTheme.colors.color_4
                },
                CircleShape,
            )
            .border(
                width = 1.5.dp,
                color = if (selected) accent else accent.copy(alpha = 0.55f),
                shape = CircleShape,
            )
            .clickable(
                interactionSource = interactionSource,
                indication = null,
                onClick = onClick,
            ),
        contentAlignment = Alignment.Center,
    ) {
        Canvas(modifier = Modifier.matchParentSize()) {
            val glowRadius = this.size.minDimension * if (selected) 0.62f else 0.42f
            drawCircle(
                brush = Brush.radialGradient(
                    colors = listOf(
                        accent.copy(alpha = if (selected) 0.38f * twinkle else 0.16f * twinkle),
                        Color.Transparent,
                    ),
                ),
                radius = glowRadius,
            )
            rotate(orbit) {
                val r = this.size.minDimension * 0.38f
                val sparkleColor = accent.copy(alpha = twinkle)
                drawFourPointStar(Offset(center.x, center.y - r), r * 0.22f, sparkleColor)
                drawFourPointStar(Offset(center.x + r * 0.82f, center.y + r * 0.18f), r * 0.14f, sparkleColor)
                drawFourPointStar(Offset(center.x - r * 0.72f, center.y + r * 0.42f), r * 0.12f, sparkleColor)
            }
        }
        Icon(
            painter = painterResource(Res.drawable.magic_wand),
            contentDescription = description,
            tint = if (selected) accent else UiKitTheme.colors.color_2,
            modifier = Modifier.size(size * 0.48f),
        )
    }
}

private fun DrawScope.drawFourPointStar(
    center: Offset,
    radius: Float,
    color: Color,
) {
    val inner = radius * 0.28f
    val path = Path().apply {
        moveTo(center.x, center.y - radius)
        lineTo(center.x + inner, center.y - inner)
        lineTo(center.x + radius, center.y)
        lineTo(center.x + inner, center.y + inner)
        lineTo(center.x, center.y + radius)
        lineTo(center.x - inner, center.y + inner)
        lineTo(center.x - radius, center.y)
        lineTo(center.x - inner, center.y - inner)
        close()
    }
    drawPath(path, color)
}
